import { BufferAttribute, BufferGeometry, Vector3 } from 'three';

/**
 * How a face's triangle normals are spread, gathered per face so a bore or
 * outer wall can be told apart from a flat end face. The scatter matrix is
 * the area-weighted sum of n*nT, row-major; its eigenvalues describe the
 * spread without needing the normals kept around.
 */
export interface FaceNormalStats {
  faceId: string;
  /** Total triangle area of the face, in model units squared. */
  area: number;
  /** Area-weighted sum of n*nT, row-major, nine entries. */
  scatter: number[];
}

function emptyStats(faceId: string): FaceNormalStats {
  return { faceId, area: 0, scatter: [0, 0, 0, 0, 0, 0, 0, 0, 0] };
}

function addTriangle(stats: FaceNormalStats, p0: Vector3, p1: Vector3, p2: Vector3): void {
  const cross = new Vector3()
    .subVectors(p1, p0)
    .cross(new Vector3().subVectors(p2, p0));

  const length = cross.length();
  if (length < 1e-12) return;

  const area = length * 0.5;
  const n = cross.divideScalar(length);
  const components = [n.x, n.y, n.z];

  stats.area += area;
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      stats.scatter[row * 3 + col] += area * components[row] * components[col];
    }
  }
}

/** Accumulates one standalone face geometry, as the legacy per-face meshes are. */
export function faceNormalStatsFromGeometry(faceId: string, geometry: BufferGeometry): FaceNormalStats {
  const stats = emptyStats(faceId);
  const position = geometry.attributes['position'] as BufferAttribute | undefined;
  if (!position || position.itemSize !== 3) return stats;

  const indices = geometry.index?.array as ArrayLike<number> | undefined;
  const count = indices ? indices.length : position.count;

  for (let i = 0; i + 2 < count; i += 3) {
    const [a, b, c] = indices ? [indices[i], indices[i + 1], indices[i + 2]] : [i, i + 1, i + 2];
    if (a >= position.count || b >= position.count || c >= position.count) continue;

    addTriangle(
      stats,
      new Vector3().fromBufferAttribute(position, a),
      new Vector3().fromBufferAttribute(position, b),
      new Vector3().fromBufferAttribute(position, c)
    );
  }

  return stats;
}

/**
 * Accumulates every face of a merged part in ONE pass. Pulling the faces out
 * one at a time rescans the whole part per face, which a part of any size
 * cannot afford on a button press.
 */
export function faceNormalStatsFromMerged(
  geometry: BufferGeometry,
  faceIdToIndex: Map<string, number>
): FaceNormalStats[] {
  const position = geometry.attributes['position'] as BufferAttribute | undefined;
  const faceIndexAttr = geometry.attributes['faceIndex'] as BufferAttribute | undefined;
  if (!position || !faceIndexAttr) return [];

  const byIndex = new Map<number, FaceNormalStats>();
  for (const [faceId, index] of faceIdToIndex) {
    byIndex.set(index, emptyStats(faceId));
  }

  const indices = geometry.index;
  const count = indices ? indices.count : position.count;

  for (let i = 0; i + 2 < count; i += 3) {
    const a = indices ? indices.getX(i) : i;
    const b = indices ? indices.getX(i + 1) : i + 1;
    const c = indices ? indices.getX(i + 2) : i + 2;
    if (a >= position.count || b >= position.count || c >= position.count) continue;

    const stats = byIndex.get(faceIndexAttr.getX(a));
    if (!stats) continue;

    addTriangle(
      stats,
      new Vector3().fromBufferAttribute(position, a),
      new Vector3().fromBufferAttribute(position, b),
      new Vector3().fromBufferAttribute(position, c)
    );
  }

  return [...byIndex.values()];
}
